import React, { useRef } from 'react';
import { BannerElement, BannerSize, DataFeed } from '../types';
import { CanvasElement } from './CanvasElement';

interface CanvasProps {
  elements: BannerElement[];
  size: BannerSize;
  zoom: number;
  showGrid: boolean;
  selectedElement: string | null;
  dataFeed: DataFeed | null;
  onSelectElement: (id: string | null) => void;
  onUpdateElement: (id: string, updates: Partial<BannerElement>) => void;
}

export const Canvas: React.FC<CanvasProps> = ({
  elements,
  size,
  zoom,
  showGrid,
  selectedElement,
  dataFeed,
  onSelectElement,
  onUpdateElement
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);

  const processContent = (content: string, dataBinding?: string) => {
    if (dataBinding && dataFeed) {
      return String(dataFeed[dataBinding] || content);
    }
    
    if (!dataFeed) return content;
    
    // Process template variables like {{field_name}}
    return content.replace(/\{\{(\w+)\}\}/g, (match, field) => {
      return String(dataFeed[field] || match);
    });
  };

  const processImageUrl = (imageUrl: string, dataBinding?: string) => {
    if (dataBinding && dataFeed) {
      return String(dataFeed[dataBinding] || imageUrl);
    }
    
    if (!dataFeed) return imageUrl;
    
    // Process template variables like {{image_url}}
    return imageUrl.replace(/\{\{(\w+)\}\}/g, (match, field) => {
      return String(dataFeed[field] || match);
    });
  };

  return (
    <div className="flex-1 bg-gray-100 p-8 overflow-auto">
      <div className="flex justify-center">
        <div className="relative">
          {/* Canvas container with proper scaling */}
          <div
            ref={canvasRef}
            className="relative bg-white shadow-lg origin-top-left"
            style={{
              width: size.width,
              height: size.height,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left'
            }}
            onClick={(e) => {
              if (e.target === canvasRef.current) {
                onSelectElement(null);
              }
            }}
          >
            {/* Grid overlay */}
            {showGrid && (
              <div
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  backgroundImage: `
                    linear-gradient(to right, #e5e7eb 1px, transparent 1px),
                    linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
                  `,
                  backgroundSize: '20px 20px'
                }}
              />
            )}

            {/* Canvas border indicator */}
            <div 
              className="absolute inset-0 border-2 border-gray-300 pointer-events-none"
              style={{ zIndex: 9999 }}
            />

            {/* Elements - properly contained within canvas bounds */}
            <div className="absolute inset-0 overflow-hidden">
              {elements
                .sort((a, b) => a.zIndex - b.zIndex)
                .map(element => (
                <CanvasElement
                  key={element.id}
                  element={element}
                  isSelected={selectedElement === element.id}
                  processedContent={
                    element.type === 'text' || element.type === 'button'
                      ? processContent(element.content || '', element.dataBinding)
                      : undefined
                  }
                  processedImageUrl={
                    element.type === 'image'
                      ? processImageUrl(element.imageUrl || '', element.dataBinding)
                      : undefined
                  }
                  onSelect={() => onSelectElement(element.id)}
                  onUpdate={(updates) => onUpdateElement(element.id, updates)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};